import React, { useState } from 'react';
import DhfFolderShell, { PhfTile } from './DhfFolderShell';
import {
  BadgeCheck, CheckCircle2, Heart, Activity, Thermometer, Moon, Zap,
  AlertTriangle, Camera, Send, LockKeyhole, Phone, MessageSquareText,
  CalendarClock, Info
} from 'lucide-react';

/* Patient Care Progress — a natural extension of the DHF Summary tab.
   Reuses the .phf-* production visual system (manila folder, cream paper,
   navy ink, teal/mint accents, Inter). No new tokens introduced. */

export default function PatientCareProgressMock() {
  const [cough, setCough] = useState(4);
  const [breath, setBreath] = useState('mild');
  const [pain, setPain] = useState(4);
  const [adhere, setAdhere] = useState(true);

  return (
    <DhfFolderShell
      activeTab="timeline"
      title={<>Care Progress <i>(Cough)</i></>}
      subtitle="A living view of your recovery — updated by you, reviewed by your doctor."
      badges={[
        { icon: <CheckCircle2 size={16} />, label: 'Ongoing care thread', tone: 'green' },
        { icon: <BadgeCheck size={16} />,   label: 'Patient-owned',        tone: 'blue' },
        { icon: <LockKeyhole size={16} />,  label: 'Feedback open until 10 Jun · 8:00 pm', tone: 'green' },
      ]}
    >
      {/* Identity band — reused pattern */}
      <div className="phf-identity">
        <div className="phf-id-cell phf-id-cell--patient">
          <span className="phf-id-avatar"><Heart size={22} color="#37589f" /></span>
          <div>
            <div className="phf-id-name">Rohan Sharma</div>
            <div className="phf-id-sub">42 / Male · +91 ••••3265</div>
          </div>
        </div>
        <div className="phf-id-cell">
          <span className="phf-id-ico"><CalendarClock size={18} /></span>
          <div><div className="phf-id-label">Started</div><div className="phf-id-value">03 Jun 26</div></div>
        </div>
        <div className="phf-id-cell">
          <span className="phf-id-ico"><CheckCircle2 size={18} /></span>
          <div><div className="phf-id-label">Latest review</div><div className="phf-id-value">07 Jun 26</div></div>
        </div>
        <div className="phf-id-cell">
          <span className="phf-id-ico"><CalendarClock size={18} /></span>
          <div><div className="phf-id-label">Next review</div><div className="phf-id-value">10 Jun 26 (in 3 days)</div></div>
        </div>
        <div className="phf-id-cell">
          <span className="phf-id-ico"><MessageSquareText size={18} /></span>
          <div><div className="phf-id-label">Shared by</div><div className="phf-id-value">Dr. Chetna Tikoo</div></div>
        </div>
      </div>

      {/* Care plan summary + Milestones — 2 tiles */}
      <div className="phf-grid">
        <PhfTile icon={<Heart size={20} />} ico="phf-ico-blue" title="Current care plan">
          <p><b>paracetamol 500 mg</b> · 1-1-1 · 3 times/day</p>
          <p style={{ color: '#526179', marginTop: 6 }}>Take medicines after food. Steam + rest as advised. Continue tracking your symptoms daily so your doctor can see how you are healing.</p>
          <div className="phf-fu-row">
            <div className="phf-bubble">Note from Dr. Chetna:<br/><b>&ldquo;Please track your symptoms for the next 3 days.&rdquo;</b></div>
          </div>
        </PhfTile>

        <PhfTile icon={<CheckCircle2 size={20} />} ico="phf-ico-green" title="Improvement milestones set for you">
          <ul className="phf-checklist" style={{ gap: 10 }}>
            {[
              { l: 'Cough frequency should reduce', t: '≤ 2/day', by: 'By 10 Jun', Icon: Activity   },
              { l: 'No fever',                       t: 'Yes',     by: 'By 10 Jun', Icon: Thermometer },
              { l: 'Sleep better at night',          t: 'Yes',     by: 'By 13 Jun', Icon: Moon        },
              { l: 'Energy level improve',            t: 'Better',  by: 'By 20 Jun', Icon: Zap         },
            ].map((m, i) => (
              <li key={i} style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <m.Icon size={16} />
                  <span>{m.l}</span>
                </span>
                <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontWeight: 750, color: '#07122f' }}>{m.t}</span>
                  <span style={{ fontSize: 12, color: '#526179' }}>{m.by}</span>
                </span>
              </li>
            ))}
          </ul>
        </PhfTile>

        {/* Progress tracker */}
        <PhfTile icon={<Activity size={20} />} ico="phf-ico-green" title="Your progress over time">
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#526179', marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, background: '#0b4f9f', borderRadius: 99 }} />Cough /day</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, background: '#007f73', borderRadius: 99 }} />Fever (°F)</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, background: '#22c7a9', borderRadius: 99 }} />Breathlessness</span>
          </div>
          <svg viewBox="0 0 420 180" style={{ width: '100%' }}>
            <line x1="30" y1="30"  x2="410" y2="30"  stroke="#eef2f8" />
            <line x1="30" y1="80"  x2="410" y2="80"  stroke="#eef2f8" />
            <line x1="30" y1="130" x2="410" y2="130" stroke="#eef2f8" />
            <polyline points="55,45 150,68 250,90 355,95" fill="none" stroke="#0b4f9f" strokeWidth="2.2" />
            {[[55,45],[150,68],[250,90],[355,95]].map(([x,y],i) => <circle key={`c${i}`} cx={x} cy={y} r="4" fill="#0b4f9f" />)}
            <polyline points="55,40 150,50 250,72 355,90" fill="none" stroke="#007f73" strokeWidth="2.2" />
            {[[55,40],[150,50],[250,72],[355,90]].map(([x,y],i) => <circle key={`f${i}`} cx={x} cy={y} r="4" fill="#007f73" />)}
            <polyline points="55,95 150,115 250,130 355,150" fill="none" stroke="#22c7a9" strokeWidth="2.2" />
            {[[55,95],[150,115],[250,130],[355,150]].map(([x,y],i) => <circle key={`b${i}`} cx={x} cy={y} r="4" fill="#22c7a9" />)}
            {['04 Jun','05 Jun','06 Jun','07 Jun'].map((d,i) => (
              <text key={d} x={55 + i*100} y="168" fontSize="11" fill="#526179" textAnchor="middle" fontFamily="Inter">{d}</text>
            ))}
          </svg>
          <p className="phf-med-note" style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="phf-chip phf-chip--green"><CheckCircle2 size={14}/> Trending: Improving</span>
            <span>Latest update on 07 Jun 26</span>
          </p>
        </PhfTile>

        {/* Update Today form */}
        <PhfTile icon={<Send size={20} />} ico="phf-ico-blue" title="Update today's progress" right={<span className="phf-chip phf-chip--blue">Date: 07 Jun 26</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Cough frequency (per day)</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, border: '1px solid #d9e3ef', borderRadius: 10, padding: '4px 6px', background: '#fff' }}>
                <button onClick={() => setCough(Math.max(0, cough-1))} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: '#526179', fontSize: 18, cursor: 'pointer' }}>−</button>
                <span style={{ fontFamily: 'Inter', fontWeight: 800, fontSize: 20, color: '#07122f', minWidth: 24, textAlign: 'center' }}>{cough}</span>
                <button onClick={() => setCough(cough+1)} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: '#526179', fontSize: 18, cursor: 'pointer' }}>+</button>
              </div>
              <div style={{ fontSize: 11, color: '#526179', marginTop: 4 }}>Number of times</div>
            </div>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Fever / Temperature (°F)</div>
              <input placeholder="e.g. 98.6" style={{ height: 38, width: '100%', border: '1px solid #d9e3ef', borderRadius: 10, padding: '0 12px', fontSize: 14, fontFamily: 'Inter' }} />
            </div>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Breathlessness</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                {['None','Mild','Moderate','Severe'].map(v => (
                  <button key={v} onClick={() => setBreath(v.toLowerCase())}
                    style={{
                      padding: '8px 4px', fontSize: 12.5, fontWeight: 600, borderRadius: 10,
                      border: '1px solid ' + (breath === v.toLowerCase() ? '#22a891' : '#d9e3ef'),
                      background: breath === v.toLowerCase() ? '#f0fbf8' : '#fff',
                      color: breath === v.toLowerCase() ? '#006b57' : '#07122f',
                      cursor: 'pointer', fontFamily: 'Inter',
                    }}>{v}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Pain / soreness (0-10)</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({length:11},(_,i) => (
                  <button key={i} onClick={() => setPain(i)}
                    style={{
                      width: 28, height: 32, fontSize: 12, borderRadius: 8, fontFamily: 'Inter', cursor: 'pointer',
                      background: pain === i ? '#0b4fd8' : '#fff',
                      color: pain === i ? '#fff' : '#07122f',
                      border: '1px solid ' + (pain === i ? '#0b4fd8' : '#d9e3ef'),
                      fontWeight: 600,
                    }}>{i}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Medicine taken as advised?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setAdhere(true)}  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid ' + (adhere ? '#22a891' : '#d9e3ef'), background: adhere ? '#f0fbf8' : '#fff', color: adhere ? '#006b57' : '#07122f', fontWeight: 700, fontFamily: 'Inter', cursor: 'pointer' }}>Yes</button>
                <button onClick={() => setAdhere(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid ' + (!adhere ? '#cc233e' : '#d9e3ef'), background: !adhere ? '#fff1f3' : '#fff', color: !adhere ? '#b91c1c' : '#07122f', fontWeight: 700, fontFamily: 'Inter', cursor: 'pointer' }}>No</button>
              </div>
            </div>
            <div>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>How are you feeling today?</div>
              <textarea rows={2} placeholder="Share in your own words…" style={{ width: '100%', border: '1px solid #d9e3ef', borderRadius: 10, padding: 10, fontSize: 13.5, fontFamily: 'Inter', resize: 'none' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="phf-id-label" style={{ marginBottom: 6 }}>Upload photo / report (optional)</div>
              <div style={{ border: '2px dashed #d9e3ef', borderRadius: 12, padding: 18, textAlign: 'center', background: '#fffdf7' }}>
                <Camera size={22} color="#526179" style={{ display: 'inline-block', marginBottom: 6 }} />
                <div style={{ fontSize: 13, color: '#526179' }}><span style={{ color: '#0b4f9f', fontWeight: 700 }}>Click to upload</span> or drag & drop · JPG, PNG, PDF up to 5 MB</div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#526179' }}><LockKeyhole size={12} style={{ verticalAlign: 'middle' }} /> Your update becomes part of your care record and is visible to your doctor.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="phf-btn-secondary">Save draft</button>
              <button className="phf-btn-primary"><Send size={16}/> Send update to doctor</button>
            </div>
          </div>
        </PhfTile>

        {/* Red flags */}
        <PhfTile icon={<AlertTriangle size={20} />} ico="phf-ico-red" title="When to get help now" risk>
          <ul className="phf-redlist">
            <li>High fever above 101°F that does not reduce with medicine</li>
            <li>Difficulty breathing or chest pain</li>
            <li>Coughing up blood</li>
            <li>Persistent vomiting, confusion, or drowsiness</li>
          </ul>
          <div className="phf-red-cta">If this is an emergency, call your local emergency number immediately.</div>
        </PhfTile>

        {/* Recent updates */}
        <PhfTile icon={<CheckCircle2 size={20} />} ico="phf-ico-blue" title="Your recent updates">
          <ul className="phf-checklist" style={{ gap: 8 }}>
            {[
              { d: '07 Jun 26', s: 'Today',       tag: 'Improving', tone: 'green' },
              { d: '06 Jun 26', s: '1 day ago',   tag: 'Improving', tone: 'green' },
              { d: '05 Jun 26', s: '2 days ago',  tag: 'Pending',   tone: 'blue'  },
              { d: '04 Jun 26', s: '3 days ago',  tag: 'Reviewed',  tone: 'blue'  },
            ].map((u, i) => (
              <li key={i} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <CheckCircle2 size={14} />
                  <span style={{ fontWeight: 700, color: '#07122f' }}>{u.d}</span>
                  <span style={{ color: '#526179', fontWeight: 500, marginLeft: 6 }}>{u.s}</span>
                </span>
                <span className={`phf-chip ${u.tone === 'green' ? 'phf-chip--green' : 'phf-chip--blue'}`}>{u.tag}</span>
              </li>
            ))}
          </ul>
        </PhfTile>
      </div>

      {/* Trust footer */}
      <div className="phf-trust">
        <div className="phf-trust-tile">
          <span className="phf-trust-ico phf-ico-green"><Info size={20} /></span>
          <div><h4>How this works</h4><p>Share your symptoms daily as advised. Your doctor reviews and adjusts your care plan.</p></div>
        </div>
        <div className="phf-trust-tile">
          <span className="phf-trust-ico phf-ico-blue"><Phone size={20} /></span>
          <div><h4>Need help?</h4><p>Request a doctor review or message Dr. Chetna Tikoo directly.</p></div>
        </div>
        <div className="phf-trust-tile">
          <span className="phf-trust-ico phf-ico-blue"><LockKeyhole size={20} /></span>
          <div><h4>Your data, your control</h4><p>Every entry is encrypted and always available in your DocSite health file.</p></div>
        </div>
      </div>
    </DhfFolderShell>
  );
}
